import { DebtRecommendation, DebtSignalLevel, WorkflowState } from '@dieterApi/debtCollection';

type ChipColor = 'default' | 'error' | 'warning' | 'success' | 'info';

export const RECOMMENDATION_LABELS: Record<DebtRecommendation, { label: string; color: ChipColor }> = {
  HANDOVER: { label: 'Übergeben', color: 'success' },
  CLARIFY: { label: 'Selbst klären', color: 'warning' },
  HOLD: { label: 'Nicht übergeben', color: 'error' },
  MISSING_DATA: { label: 'Daten fehlen', color: 'error' },
  WAIT: { label: 'Mahnung läuft', color: 'default' },
};

export const RECOMMENDATION_ORDER: DebtRecommendation[] = ['HANDOVER', 'CLARIFY', 'HOLD', 'MISSING_DATA', 'WAIT'];

export const SIGNAL_CHIP: Record<DebtSignalLevel, { color: ChipColor; variant: 'filled' | 'outlined' }> = {
  red: { color: 'error', variant: 'filled' },
  yellow: { color: 'warning', variant: 'filled' },
  green: { color: 'success', variant: 'outlined' },
  block: { color: 'error', variant: 'outlined' },
  wait: { color: 'default', variant: 'filled' },
  info: { color: 'default', variant: 'outlined' },
};

export const SUBSCRIPTION_LABELS: Record<string, string> = {
  active: 'aktiv',
  past_due: 'überfällig',
  unpaid: 'unbezahlt',
  trialing: 'Test',
};

export const formatEur = (value: number) =>
  value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });

export const formatDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString('de-DE') : '—';

export const stripeCustomerUrl = (id: string) => `https://dashboard.stripe.com/customers/${id}`;

export const WORKFLOW_LABELS: Record<WorkflowState, { label: string; color: ChipColor }> = {
  DECIDE: { label: 'Zu entscheiden', color: 'info' },
  FOLLOW_UP: { label: 'Nachforderung', color: 'info' },
  DUNNING: { label: 'Mahnung läuft', color: 'default' },
  IN_PROGRESS: { label: 'Übergabe läuft', color: 'warning' },
  AT_DEBTIST: { label: 'Bei Debtist', color: 'success' },
  PARKED: { label: 'Zurückgestellt', color: 'default' },
  WRITTEN_OFF: { label: 'Ausgebucht', color: 'default' },
  CLOSED: { label: 'Abgeschlossen', color: 'default' },
};

export const EVENT_LABELS: Record<string, string> = {
  HANDOVER_STARTED: 'Übergabe gestartet',
  HANDOVER_FINISHED: 'Übergabe abgeschlossen',
  HOLD: 'Zurückgestellt',
  CLARIFY: 'Selbst klären',
  WRITE_OFF: 'Ausgebucht',
  REOPEN: 'Wieder geöffnet',
  DEBTIST_FEEDBACK_REQUIRED: 'Rückfrage von Debtist',
  FEEDBACK_ANSWERED: 'Rückfrage beantwortet',
  DEBTIST_COLLECTED: 'Von Debtist eingetrieben',
  DEBTIST_CLOSED: 'Von Debtist geschlossen',
  DEBTIST_CANCELLED: 'Bei Debtist storniert',
  DIRECT_PAYMENT: 'Direktzahlung gemeldet',
  DIRECT_PAYMENT_STRIPE: 'In Stripe bezahlt, an Debtist gemeldet',
  DIRECT_PAYMENT_FAILED: 'Direktzahlung nicht gemeldet (Fehler)',
  WITHDRAWN: 'Zurückgezogen',
  ALL_SETTLED: 'Alle Rechnungen erledigt',
};

export const handoverTargetLabel = (target: string | null | undefined) =>
  target === 'production' ? 'Debtist (Produktion)' : 'Debtist-Sandbox (Test)';

export const DEBTIST_STATUS_LABELS: Record<string, string> = {
  Open: 'in Prüfung',
  ReminderLevel: 'Mahnstufe',
  EncashmentLevel: 'Inkasso',
  JudicialLevel: 'gerichtlich',
  Paid: 'bezahlt',
  Closed: 'geschlossen',
  Cancelled: 'zurückgezogen',
  Paused: 'pausiert',
};

export const WITHDRAW_REASON_LABELS: Record<string, string> = {
  PaidDirectlyToCreditor: 'Kunde hat direkt an uns gezahlt',
  InvalidOrDisputedClaim: 'Forderung strittig oder falsch',
  CreditorWishWithoutReason: 'Kulanz / ohne Angabe',
  CaseNotEconomical: 'nicht wirtschaftlich',
};

// Debtist-Aktivitätstypen (Auswahl), unbekannte erscheinen als Code
export const DEBTIST_ACTIVITY_LABELS: Record<string, string> = {
  OutboundMail: 'Mail an Schuldner',
  OutboundLetter: 'Brief an Schuldner',
  OutboundCall: 'Anruf beim Schuldner',
  OutboundSms: 'SMS an Schuldner',
  OutboundWhatsapp: 'WhatsApp an Schuldner',
  Response: 'Antwort des Schuldners',
  CreditorFeedback: 'Antwort von uns',
  CreditorInformation: 'Info an uns',
  CreditorDecision: 'Entscheidung von uns',
  InternalDecision: 'Debtist intern',
  InternalFeedback: 'Debtist intern',
  Research: 'Recherche',
  JudicialActivity: 'gerichtlich',
  InsolvencyActivity: 'Insolvenz',
  Other: 'Sonstiges',
};
